// Job 1: assigning a network tariff to a site appeared to do nothing — the panel
// still read "No network tariffs assigned to this site".
//
// `bv_site_network` holds one row per (org, distributor, tariff_code, bucket)
// since migration 060, so a single tariff is many rows. Two consequences the UI
// was not handling:
//
//   * The org's row count runs into the thousands, and the loader had no
//     pagination — Supabase caps an unbounded select at 1000 rows, ordered by
//     tariff_code, so codes late in the alphabet silently did not exist as far as
//     the screen was concerned. That is the root cause of job 1 and very likely
//     of "tariffs not appearing on site pages" and "all labelled as Ausgrid".
//   * A picker built straight from those rows repeats each code once per bucket.

import type { BvSiteNetwork, BvTariffPeriod } from '../types/billValidationTypes';

/** Codes are compared folded — the table is populated by bulk upload and by
 *  hand, so the same code arrives as "EA010", " ea010 " and "Ea010". */
const foldCode = (code?: string | null): string => (code ?? '').trim().toLowerCase();

/** One row per tariff code, for pickers. The rate tables still want every row. */
export const uniqueByTariffCode = (rows: BvSiteNetwork[]): BvSiteNetwork[] => {
  const seen = new Set<string>();
  const out: BvSiteNetwork[] = [];
  for (const row of rows) {
    const key = (row.tariff_code ?? '').trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
};

/**
 * Codes assigned to a site that have no definition in the loaded set.
 *
 * Rendering these as "nothing assigned" is what made the original bug invisible:
 * the site did have codes, they just could not be resolved. Comparison ignores
 * case and surrounding space so a code that differs only in formatting is not
 * reported as missing.
 */
export const missingTariffCodes = (
  assignedCodes: string[] | null | undefined,
  rows: BvSiteNetwork[],
): string[] => {
  const defined = new Set(rows.map((r) => (r.tariff_code ?? '').trim().toLowerCase()));
  return (assignedCodes ?? [])
    .filter((code) => {
      const key = (code ?? '').trim().toLowerCase();
      return !!key && !defined.has(key);
    });
};

/** Rows for the codes assigned to a site, tolerant of case and stray whitespace. */
export const rowsForAssignedCodes = (
  assignedCodes: string[] | null | undefined,
  rows: BvSiteNetwork[],
): BvSiteNetwork[] => {
  const wanted = new Set((assignedCodes ?? []).map((c) => (c ?? '').trim().toLowerCase()));
  return rows.filter((r) => wanted.has((r.tariff_code ?? '').trim().toLowerCase()));
};

/**
 * Job 35/50: the panel was labelling every site's tariffs with one distributor,
 * so a real assignment could not be told from a wrong one. These helpers make the
 * distributor of the loaded rows explicit.
 *
 * Distinct distributor names among the given rows, folding aliases, case and
 * stray whitespace together. `sameDistributor` decides whether two names refer to
 * the same physical distributor.
 */
export const distinctDistributors = (
  rows: BvSiteNetwork[],
  sameDistributor: (a?: string | null, b?: string | null) => boolean,
): string[] => {
  const out: string[] = [];
  for (const r of rows) {
    const name = (r.distributor ?? '').trim();
    if (!name) continue;
    if (!out.some((existing) => sameDistributor(existing, name))) out.push(name);
  }
  return out;
};

/**
 * Codes assigned to a site whose rows span more than one distributor. Shown once
 * arbitrarily this reads as a clean assignment; it needs flagging instead.
 */
export const codesWithMultipleDistributors = (
  assignedCodes: string[] | null | undefined,
  rows: BvSiteNetwork[],
  sameDistributor: (a?: string | null, b?: string | null) => boolean,
): string[] => {
  const out: string[] = [];
  for (const code of assignedCodes ?? []) {
    const key = foldCode(code);
    if (!key) continue;
    const codeRows = rows.filter((r) => foldCode(r.tariff_code) === key);
    if (distinctDistributors(codeRows, sameDistributor).length > 1) out.push(code);
  }
  return out;
};

/**
 * The values an org already uses for a catalog field, for a picker.
 *
 * Distributor, category and price type were typed by hand in three separate
 * forms writing the same table, which is why `distributorsMatch` needs an alias
 * list to reconcile "Ausgrid" with "Ausgrid Networks". Offering back what is
 * already recorded is what stops the next spelling being invented; the pickers
 * still accept a genuinely new value, they just no longer invite a typo.
 *
 * Derived from the rows already in memory — no extra query, nothing to migrate.
 */
export const distinctFieldValues = (
  rows: BvSiteNetwork[],
  field: keyof BvSiteNetwork,
): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const row of rows) {
    const raw = row[field];
    if (typeof raw !== 'string') continue;
    const value = raw.trim();
    const key = value.toLowerCase();
    if (!value || seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out.sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }));
};

/** The shape the code picker needs — a code, and the name shown beside it. */
export type TariffPickOption = { tariff_code: string; tariff_name?: string | null };

/**
 * Options matching what has been typed, by code or by name.
 *
 * A distributor publishes hundreds of codes, so the picker has to be narrowed by
 * typing. Name is searched as well as code because a validator reading a bill
 * often has the wording ("LV Demand") and not the number. Plain substring, not
 * word-prefix: codes arrive as "017, 018*" and a prefix match would never find
 * the second half.
 */
export const filterTariffOptions = <T extends TariffPickOption>(
  options: T[],
  term: string,
): T[] => {
  const needle = (term ?? '').trim().toLowerCase();
  if (!needle) return options;
  return options.filter((o) =>
    (o.tariff_code ?? '').toLowerCase().includes(needle) ||
    (o.tariff_name ?? '').toLowerCase().includes(needle));
};

/** Where a site's window on a tariff code sits relative to today. */
export type TariffPeriodStatus = 'upcoming' | 'current' | 'ended' | 'unknown';

/**
 * A code assigned with a `from` in the future already saved correctly, but the
 * page drew it identically to a live tariff — so "add a future tariff" looked
 * impossible even though it worked. Naming the state is what makes it visible.
 *
 * Both dates are inclusive: a window starting or ending today is one the site is
 * on today. ISO `YYYY-MM-DD` compares correctly as a string, so no Date parsing
 * (and no timezone shift) is involved.
 */
export const tariffPeriodStatus = (
  period: BvTariffPeriod | undefined,
  today: string,
): TariffPeriodStatus => {
  const from = period?.from ?? null;
  const to = period?.to ?? null;
  if (!from && !to) return 'unknown';
  if (from && from > today) return 'upcoming';
  if (to && to < today) return 'ended';
  return 'current';
};

/**
 * Bucket rows ordered as a timeline — oldest window first, codes with no
 * recorded start last.
 *
 * The rows arrive in catalog order, so a site that moved EA225 → EA256 read in
 * whichever order the catalog happened to hold them. Every bucket row of a code
 * stays together and keeps its original order: the window belongs to the CODE,
 * so splitting its rows would imply buckets could differ.
 */
export const sortRowsByTariffPeriod = (
  rows: BvSiteNetwork[],
  dates: Record<string, BvTariffPeriod> | null | undefined,
): BvSiteNetwork[] => {
  const from = new Map<string, string>();
  for (const [code, period] of Object.entries(dates ?? {})) {
    const key = foldCode(code);
    if (key && period?.from) from.set(key, period.from);
  }

  // Group by code, first appearance wins the group's position.
  const groups: Array<{ key: string; rows: BvSiteNetwork[] }> = [];
  const byKey = new Map<string, BvSiteNetwork[]>();
  for (const row of rows) {
    const key = foldCode(row.tariff_code);
    let bucket = byKey.get(key);
    if (!bucket) {
      bucket = [];
      byKey.set(key, bucket);
      groups.push({ key, rows: bucket });
    }
    bucket.push(row);
  }

  // `sort` is stable, so groups sharing a date — and every undated group — keep
  // the order they were assigned in.
  const ordered = groups.slice().sort((a, b) => {
    const fa = from.get(a.key);
    const fb = from.get(b.key);
    if (fa && fb) return fa < fb ? -1 : fa > fb ? 1 : 0;
    if (fa) return -1;
    if (fb) return 1;
    return 0;
  });

  return ordered.flatMap((g) => g.rows);
};
